import clsx from "clsx";
import { useCallback, useEffect, useRef, useState } from "react";

import { serverUpdateTimerDuration } from "api/api-server-actions";
import { t } from "i18n/i18n";
import { CloseButton } from "mds/components/CloseButton";
import { IconText } from "mds/components/IconText";
import { TextField } from "mds/components/TextField";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { ChevronDownIcon, ChevronUpIcon, CircleCheckIcon, ClockIcon } from "mds/icons";
import { useAppSelector } from "store/index";
import { selectActiveClassSessionId, selectClassTimerEndsAt } from "store/selectors";
import { toastLocalizedOperationCatcher } from "utils/alerts";
import { useTimeRemaining } from "utils/hooks/use-time-remaining";
import {
  MAX_TIMER_SECONDS,
  MIN_TIMER_SECONDS,
  TIMER_STEP_SECONDS,
  formatSecondsToMmSs,
  parseMmSs,
} from "utils/timer";

const CLOSE_MENU_DELAY_MS = 1500;

type TimerControlsProps = {
  onClose?: () => void;
};

const TimerControls = ({ onClose }: TimerControlsProps) => {
  const classSessionId = useAppSelector(selectActiveClassSessionId);
  const endTime = useAppSelector(selectClassTimerEndsAt);
  const endDate = endTime ? new Date(endTime) : null;
  const { timeRemainingMs, timeRemainingStrPadded } = useTimeRemaining(endDate);
  const hasSetFromTimer = useRef(false);
  const [inputValue, setInputValue] = useState("10:00");
  const [timerUpdated, setTimerUpdated] = useState(false);

  useEffect(() => {
    if (!endTime) {
      hasSetFromTimer.current = false;
      return;
    }
    if (!hasSetFromTimer.current && timeRemainingMs !== null) {
      setInputValue(timeRemainingStrPadded);
      hasSetFromTimer.current = true;
    }
  }, [endTime, timeRemainingMs, timeRemainingStrPadded]);

  const durationSeconds = parseMmSs(inputValue);
  const isValid = durationSeconds !== null && durationSeconds <= MAX_TIMER_SECONDS;
  const canStart = isValid && durationSeconds >= MIN_TIMER_SECONDS;

  const adjustDuration = useCallback((delta: number) => {
    setInputValue((prev) => {
      const current = parseMmSs(prev) ?? 600;
      const next = Math.max(0, Math.min(MAX_TIMER_SECONDS, current + delta));
      return formatSecondsToMmSs(next);
    });
  }, []);

  const onStartTimer = useCallback(async () => {
    if (!classSessionId || !canStart || durationSeconds === null) return;
    try {
      await serverUpdateTimerDuration(classSessionId, durationSeconds);
      onClose?.();
    } catch (err: unknown) {
      toastLocalizedOperationCatcher("failed_to_set_timer")(err);
    }
  }, [classSessionId, canStart, durationSeconds, onClose]);

  const onUpdateTimer = useCallback(async () => {
    if (!classSessionId || !isValid || durationSeconds === null) return;
    try {
      await serverUpdateTimerDuration(classSessionId, durationSeconds);
      setTimerUpdated(true);
      setTimeout(() => {
        setTimerUpdated(false);
        onClose?.();
      }, CLOSE_MENU_DELAY_MS);
    } catch (err: unknown) {
      toastLocalizedOperationCatcher("failed_to_set_timer")(err);
    }
  }, [classSessionId, isValid, durationSeconds, onClose]);

  const onEndTimer = useCallback(async () => {
    if (!classSessionId) return;
    try {
      await serverUpdateTimerDuration(classSessionId, 0);
      onClose?.();
    } catch (err: unknown) {
      toastLocalizedOperationCatcher("failed_to_end_timer")(err);
    }
  }, [classSessionId, onClose]);

  return (
    <div className="group/themable flex flex-col gap-3 p-2 text-white" data-dark>
      <div className="flex items-center justify-between">
        <IconText iconStart={<ClockIcon />} text={t("footer.tool.timer_title")} />
        <CloseButton onClick={onClose} />
      </div>

      <div className="flex items-center rounded-lg border border-black-tint-40 p-0.5">
        <TextField
          className="flex-1 border-none bg-transparent px-3 py-2 text-white outline-none"
          size="s"
          value={inputValue}
          wrapperClassName="flex-1"
          isEditing
          onChange={(e) => setInputValue(e.target.value)}
        />
        <div className="flex flex-col">
          <button
            aria-label={t("footer.tool.timer_increment")}
            className="flex items-center justify-center rounded-t-md bg-black-tint-30 p-0.5 text-white hover:bg-black-tint-40"
            type="button"
            onClick={() => adjustDuration(TIMER_STEP_SECONDS)}
          >
            <ChevronUpIcon size="xs" />
          </button>
          <button
            aria-label={t("footer.tool.timer_decrement")}
            className="flex items-center justify-center rounded-b-md bg-black-tint-30 p-0.5 text-white hover:bg-black-tint-40"
            type="button"
            onClick={() => adjustDuration(-TIMER_STEP_SECONDS)}
          >
            <ChevronDownIcon size="xs" />
          </button>
        </div>
      </div>

      {endTime ? (
        <>
          <SquareButton
            className={clsx("w-full", timerUpdated && "pointer-events-none")}
            kind={timerUpdated ? "success" : "tertiary"}
            size="s"
            onClick={onUpdateTimer}
          >
            {timerUpdated ? (
              <IconText iconStart={<CircleCheckIcon />} text={t("footer.tool.timer_updated")} />
            ) : (
              t("footer.tool.timer_update")
            )}
          </SquareButton>
          <SquareButton className="w-full" kind="tertiary" size="s" onClick={onEndTimer}>
            {t("footer.tool.timer_end")}
          </SquareButton>
        </>
      ) : (
        <SquareButton
          className="w-full"
          disabled={!canStart}
          kind="tertiary"
          size="s"
          onClick={onStartTimer}
        >
          {t("footer.tool.timer_start")}
        </SquareButton>
      )}
    </div>
  );
};

export { TimerControls };
